import { Formik } from 'formik';
import { Form } from 'formik';
import { isEqual } from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { company } from '../../../../../models/entities/company/company';
import { companyAdminUpdateUserPayload, userInfo } from '../../../../../models/entities/user';
import Button from '../../../../shared/SmallComponents/Button';
import AccordionCheckBoxsesField from './components/accordionCheckBoxsesField';
import { Modal } from 'antd';
import Loader from '../../../../shared/SmallComponents/Loader';
import ClaimsFormField from './components/claimsFormField';
import EditableInputField from './components/EditableInputField';
import EditableSelectField from './components/EditableSelectField';
import { eShipmentTypes } from '../../../../../models/entities/shipment/shipmentTypes';
import { companiesRelationsResponseType } from '../../../../../models/entities/company/companiesRelation';

type Props = {
    user: userInfo;
    companies: company[];
    relations: companiesRelationsResponseType;
    updateUserDetails: (details: companyAdminUpdateUserPayload, userId: string) => Promise<void>;
    resetUserPassword: (userId: string, email: string) => Promise<void>;
    deleteUser: (userId: string) => Promise<void>;
    restoreUser: (userId: string) => Promise<void>;
    localization: any;
};

const validationSchema = Yup.object().shape({
    firstName: Yup.string().max(50, 'First name must be less than 50 characters'),
    lastName: Yup.string().max(50, 'Last name must be less than 50 characters'),
    email: Yup.string().email('Invalid email format').max(50, 'Email must be less than 50 characters'),
    phoneNumber: Yup.string().max(40, 'Phone number must be less than 40 characters'),
    role: Yup.string().max(50, 'Role must be less than 50 characters'),
    companyName: Yup.string().max(100, 'Company name must be less than 100 characters'),
    birthDate: Yup.string().test(
        'date-format',
        'Birth date must be in YYYY-MM-DD format',
        function(value) {
            if (!value || value.trim() === '') {
                return true; // Allow empty values
            }
            return /^\d{4}-\d{2}-\d{2}$/.test(value);
        }
    )
});
const EditUserForm = ({ user, companies, updateUserDetails, localization, resetUserPassword, deleteUser, restoreUser, relations }: Props) => {
    const [formChanged, setFormChanged] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const relatedCompaniesIds: string[] = relations.requested.map((relation) => relation.companyId);

    const relatedCompaniesIdsWithSubCompanies: { companyId: string; subCompaniesIds: string[] }[] = relations.requested.map((relation) => ({
        companyId: relation.companyId,
        subCompaniesIds: relation.subCompaniesIds
    }));

    const initialValues: any = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        role: user.role || '',
        birthDate: user.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : '',
        companyId: user.companyId || '',
        claims: user.claims,
        allowedCompanies: (user.allowedCompanies as Array<string>) || [],
        allowedShipmentTypes: user.allowedShipmentTypes || [],
        allowedRelatedCompanies: relatedCompaniesIds.filter(
            (id) => !(user.notAllowedRelatedCompanies || []).some((notAllowed) => notAllowed.companyId === id)
        )
    };

    const hasChanged = (values: any) => {
        const filteredClaimsValues: { [key: string]: any } = {};
        Object.entries(values.claims || {}).forEach(([key, value]) => (Number(value) ? (filteredClaimsValues[key] = value) : undefined));

        const filteredClaimsInitialValues: { [key: string]: any } = {};
        Object.entries(initialValues.claims || {}).forEach(([key, value]) =>
            Number(value) ? (filteredClaimsInitialValues[key] = value) : undefined
        );

        if (Object.keys(filteredClaimsInitialValues).length !== Object.keys(filteredClaimsValues).length) return true;

        // Check basic user fields
        if (values.firstName !== initialValues.firstName) return true;
        if (values.lastName !== initialValues.lastName) return true;
        if (values.email !== initialValues.email) return true;
        if (values.phoneNumber !== initialValues.phoneNumber) return true;
        if (values.role !== initialValues.role) return true;
        if (values.birthDate !== initialValues.birthDate) return true;
        if (values.companyId !== initialValues.companyId) return true;

        // Check arrays
        if (!isEqual(initialValues.allowedCompanies, values.allowedCompanies)) return true;
        if (!isEqual(initialValues.allowedShipmentTypes, values.allowedShipmentTypes)) return true;
        if (!isEqual(initialValues.allowedRelatedCompanies, values.allowedRelatedCompanies)) return true;
        if (!isEqual(filteredClaimsInitialValues, filteredClaimsValues)) return true;

        return false;
    };

    const onSubmit = async (values: any) => {
        if (isSubmit || !formChanged) return;

        setIsSubmit(true);

        // Find the selected company name based on company ID
        const selectedCompany = companies.find(company => company.id === values.companyId);
        const companyName = selectedCompany ? selectedCompany.name : '';

        const payload: companyAdminUpdateUserPayload = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phoneNumber,
            role: values.role,
            companyName: companyName,
            claims: values.claims,
            allowedCompanies: (values.allowedCompanies as Array<string>) || [],
            allowedShipmentTypes: values.allowedShipmentTypes || [],
            notAllowedRelatedCompanies: relatedCompaniesIdsWithSubCompanies.filter(
                (relatedIds) => !values.allowedRelatedCompanies.includes(relatedIds.companyId)
            )
        };

        // Only include birthDate if it has a valid value
        if (values.birthDate && values.birthDate.trim() !== '') {
            payload.birthDate = values.birthDate;
        }

        await updateUserDetails(payload, user.id);
        setIsSubmit(false);
    };

    return (
        <Wrapper>
            <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {(formik) => {
                    const { isValid, values, setFieldValue } = formik;
                    setFormChanged(hasChanged(values));
                    return (
                        <>
                            <Form>
                                <HeaderWrapper>
                                    <div style={{ display: 'flex' }}>
                                        <Title>
                                            {user.firstName} {user.lastName}
                                        </Title>
                                        <Button
                                            margin="0 15px"
                                            padding="0"
                                            width="auto"
                                            minHeight="auto"
                                            height="27px"
                                            buttonType="clean"
                                            textColor={!user.deletedAt ? '#fb3232' : undefined}
                                            onClick={async () => {
                                                Modal.confirm({
                                                    zIndex: 1100,
                                                    content: <div>{localization.are_you_sure}</div>,
                                                    async onOk() {
                                                        if (!user.deletedAt) {
                                                            await deleteUser(user.id);
                                                        } else {
                                                            await restoreUser(user.id);
                                                        }
                                                    },
                                                    onCancel() {}
                                                });
                                            }}
                                            disabled={false}
                                        >
                                            {!user.deletedAt ? localization.delete : localization.restore}
                                        </Button>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <Button
                                            margin="0 15px 0 0"
                                            padding="0"
                                            width="auto"
                                            minHeight="auto"
                                            height="27px"
                                            buttonType="clean"
                                            onClick={async () => {
                                                await resetUserPassword(user.id, user.email);
                                            }}
                                            //disabled={user.shouldResetPassword}
                                        >
                                            {localization.reset_pass}
                                        </Button>
                                        <Button
                                            margin="0"
                                            padding="0"
                                            width="auto"
                                            minHeight="auto"
                                            height="27px"
                                            buttonType="clean"
                                            type="submit"
                                            disabled={!formChanged}
                                        >
                                            {isSubmit ? <Loader width="20px" marginTop="0px" showText={false} /> : localization.submit_btn}
                                        </Button>
                                    </div>
                                </HeaderWrapper>
                                <DetailsContainer>
                                    <EditableInputField
                                        fieldName="firstName"
                                        label={localization.fields?.firstName || 'First Name'}
                                        maxLength={50}
                                        placeholder="Enter first name"
                                    />
                                    <EditableInputField
                                        fieldName="lastName"
                                        label={localization.fields?.lastName || 'Last Name'}
                                        maxLength={50}
                                        placeholder="Enter last name"
                                    />
                                    <EditableInputField
                                        fieldName="email"
                                        label={localization.fields?.email || 'Email'}
                                        type="email"
                                        maxLength={50}
                                        placeholder="Enter email address"
                                    />
                                    <EditableInputField
                                        fieldName="phoneNumber"
                                        label={localization.fields?.phoneNumber || 'Phone Number'}
                                        type="tel"
                                        inputMode="tel"
                                        maxLength={40}
                                        placeholder="Enter phone number"
                                    />
                                    <EditableInputField
                                        fieldName="role"
                                        label={localization.fields?.role || 'Role'}
                                        maxLength={50}
                                        placeholder="Enter role"
                                    />
                                    <EditableInputField
                                        fieldName="birthDate"
                                        label={localization.fields?.birthDate || 'Birth Date'}
                                        type="date"
                                        placeholder="Select birth date"
                                    />
                                    <EditableSelectField
                                        fieldName="companyId"
                                        label={localization.fields?.companyName || 'Company'}
                                        options={companies.map(company => ({
                                            label: company.name,
                                            value: company.id
                                        }))}
                                        placeholder="Select a company"
                                    />
                                </DetailsContainer>
                                <AccordionWrapper>
                                    <AccordionWrapper>
                                        <AccordionCheckBoxsesField
                                            fieldName="allowedCompanies"
                                            title={localization.allowed_companies}
                                            options={companies.map((company) => ({
                                                label: company.name,
                                                value: company.id
                                            }))}
                                        />
                                        <AccordionCheckBoxsesField
                                            fieldName="allowedShipmentTypes"
                                            title={localization.allowed_shipment_types}
                                            options={Object.entries(eShipmentTypes).map(([typeCode, value]) => ({
                                                label: value,
                                                value: typeCode
                                            }))}
                                        />
                                        <AccordionCheckBoxsesField
                                            fieldName="allowedRelatedCompanies"
                                            title={localization.allowed_related_companies}
                                            options={relations.requested.map((relation) => ({
                                                label: relation.companyName,
                                                value: relation.companyId
                                            }))}
                                        />
                                    </AccordionWrapper>
                                </AccordionWrapper>
                                <AccordionWrapper>
                                    <PermissionsTitle>{localization.permissions}:</PermissionsTitle>
                                    <ClaimsFormField fieldName="claims" localization={localization.claims} />
                                </AccordionWrapper>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </Wrapper>
    );
};

const Wrapper = styled.div``;

const HeaderWrapper = styled.div`
    display: flex;
    height: auto;
    padding: 10px 5px;
    margin: 0 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
    justify-content: space-between;
`;

const AccordionWrapper = styled.div`
    margin-top: 20px;
`;

const PermissionsTitle = styled.div`
    font-size: 18px;
    font-weight: 500;
    padding: 10px 5px;
`;

const Title = styled.div`
    font-size: 18px;
    font-weight: 500;
`;

const DetailsContainer = styled.div`
    padding: 20px;
    background-color: #f7f7f700;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(189px, 1fr));
    grid-gap: 1.7rem;
`;


export default EditUserForm;
